import { create } from "zustand";

/** Prefix for task row droppable ids (`task:{id}`). */
export const TRACKING_TASK_DROP_PREFIX = "task:";

/** Passenger currently being dragged (overlay, source task highlight). */
export type TrackingDragPayload = {
    id: string;
    name: string;
    /** Source task id — the task the passenger is being dragged out of. */
    taskId: string;
    /** Arrival/departure flag — must match the destination task's `isArrival` to be a valid drop. */
    isFlightArrival: boolean;
};

/** Drop-target data — destination task. */
export type TrackingOverTarget = {
    taskId: string;
    isArrival: boolean;
};

/** Single DnD UI state: what is dragged + which task row is highlighted (if any). */
export type TrackingDndSession = {
    drag: TrackingDragPayload;
    target: TrackingOverTarget | null;
};

type TrackingDndState = {
    overData: TrackingDndSession | null;
};

const initialState: TrackingDndState = {
    overData: null,
};

type TrackingDndActions = {
    actions: {
        beginDrag: (drag: TrackingDragPayload) => void;
        setTarget: (target: TrackingOverTarget | null) => void;
        resetSession: () => void;
    };
};

export const useTrackingDndStore = create<TrackingDndState & TrackingDndActions>()((set) => ({
    ...initialState,
    actions: {
        beginDrag: (drag) => set({ overData: { drag, target: null } }),
        setTarget: (target) =>
            set((s) => {
                if (!s.overData) return s;
                return { overData: { ...s.overData, target } };
            }),
        resetSession: () => set({ overData: null }),
    },
}));

/** Full session (`null` when not dragging). */
export const useTrackingDndOverData = () => useTrackingDndStore((s) => s.overData);

export const useTrackingDndDrag = () => useTrackingDndStore((s) => s.overData?.drag ?? null);

export const useTrackingDndOverTaskId = () => useTrackingDndStore((s) => s.overData?.target?.taskId ?? null);

/** Whether the currently hovered target task accepts the dragged passenger (`null` when not over a task). */
export const useTrackingDndOverTaskIsValid = () =>
    useTrackingDndStore((s) => {
        const { drag, target } = s.overData ?? {};
        if (!drag || !target) return null;
        return drag.isFlightArrival === target.isArrival;
    });

export const useTrackingDndActions = () => useTrackingDndStore((s) => s.actions);
